/* "How I work" — a single-focus page with Sopfie's short film on a warm beige
   canvas, her mark in the corner, and a quiet sentence to introduce it. */
import { useLang } from "../context/LanguageContext";

const VIDEO_URL =
  "https://customer-assets.emergentagent.com/job_whole-being-coach/artifacts/73k5oxo6_IMG_1920.mov";

export default function HowIWork() {
  const { t } = useLang();
  return (
    <section
      className="min-h-screen pt-24 pb-20"
      style={{ backgroundColor: "#F3EBE1" /* warm cream — var(--bg-2) */ }}
      data-testid="how-i-work-page"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <p className="overline mb-4 text-[color:var(--accent)]">{t("hiw.overline")}</p>
        <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[color:var(--ink)] tracking-tight leading-[1.05]">
          {t("hiw.title.a")}<br />
          <em className="text-[color:var(--accent)] not-italic">{t("hiw.title.b")}</em>
        </h1>

        {/* A reflective quote to land the viewer before the video */}
        <figure className="mt-12 max-w-3xl" data-testid="how-i-work-quote">
          <blockquote className="font-serif italic font-light text-2xl sm:text-3xl text-[color:var(--ink)] leading-[1.35]">
            {t("hiw.quote")}
          </blockquote>
        </figure>

        {/* Video canvas — warm beige stage with soft shadow + logo watermark */}
        <div
          className="relative mt-10 mx-auto max-w-xl rounded-3xl overflow-hidden shadow-[0_30px_80px_-40px_rgba(42,39,36,0.35)] border border-[color:var(--line-soft)]"
          style={{ backgroundColor: "#EFE6D9" }}
          data-testid="how-i-work-video-stage"
        >
          <video
            className="w-full h-auto block"
            src={VIDEO_URL}
            controls
            playsInline
            preload="metadata"
            data-testid="how-i-work-video"
          >
            Sorry, your browser can't play this video.
          </video>

          {/* Text-only watermark — top-left */}
          <div
            className="absolute top-3 left-3 sm:top-4 sm:left-4 z-20 px-3 py-1.5 rounded-full bg-[rgba(251,249,246,0.78)] backdrop-blur-md ring-1 ring-[color:var(--line-soft)]"
            data-testid="how-i-work-watermark"
          >
            <span className="font-serif text-xs sm:text-sm tracking-tight text-[color:var(--ink)]">
              Youfitbysopfie
              <span className="text-[color:var(--accent)]">.com</span>
            </span>
          </div>
        </div>

        {/* Small caption under the video */}
        <p className="mt-5 text-xs sm:text-sm text-[color:var(--ink-3)] italic max-w-xl">
          {t("hiw.caption")}
        </p>

        {/* Three pillars — tied to the brand language */}
        <div className="mt-14 grid sm:grid-cols-3 gap-6">
          {[
            { id: "listen", h: t("hiw.pillar.listen.title"), p: t("hiw.pillar.listen.body") },
            { id: "build",  h: t("hiw.pillar.build.title"),  p: t("hiw.pillar.build.body") },
            { id: "home",   h: t("hiw.pillar.home.title"),   p: t("hiw.pillar.home.body") },
          ].map((c) => (
            <div
              key={c.id}
              className="rounded-2xl bg-[var(--bg)] border border-[color:var(--line-soft)] p-6"
              data-testid={`how-i-work-pillar-${c.id}`}
            >
              <p className="font-serif text-xl text-[color:var(--ink)]">{c.h}</p>
              <p className="text-sm text-[color:var(--ink-2)] mt-2 leading-relaxed">{c.p}</p>
            </div>
          ))}
        </div>

        {/* PUBLISHED ARTICLE */}
        <section className="mt-20 md:mt-24" data-testid="how-i-work-article">
          <div className="text-center">
            <p className="overline mb-3 text-[color:var(--accent)]">{t("hiw.press.label")}</p>
            <p className="text-sm text-[color:var(--ink-3)] italic">{t("hiw.press.subtitle")}</p>
          </div>

          {/* Article — translated dynamically */}
          <article className="mt-8 max-w-3xl mx-auto card-soft p-8 sm:p-10" data-testid="how-i-work-article-text">
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-[color:var(--ink)] leading-tight tracking-tight">
              {t("hiw.article.title")}
            </h2>
            <div className="mt-6 space-y-5 text-[color:var(--ink-2)] leading-relaxed text-base sm:text-lg">
              <p>{t("hiw.article.p1")}</p>
              <p>{t("hiw.article.p2")}</p>
              <p>{t("hiw.article.p3")}</p>
            </div>
            <div className="mt-8 pt-6 border-t border-[color:var(--line-soft)]">
              <a
                href="https://elle.dk/skoenhed/kropsterapi-hvad-er-det-egentlig-det-gar-ud-pa/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[color:var(--accent)] hover:text-[color:var(--ink)] transition-colors"
                data-testid="how-i-work-article-link"
              >
                {t("hiw.article.link")}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M7 17 17 7" /><path d="M7 7h10v10" />
                </svg>
              </a>
            </div>
          </article>
        </section>
      </div>
    </section>
  );
}
